import { useEffect, useRef } from 'react';

interface AutoPlayVideoProps {
  src: string;
  poster?: string;
  className?: string;
}

/**
 * A decorative looping preview that does not request its video until it is
 * close to the viewport. Once loaded, offscreen previews are paused so grids
 * do not keep dozens of video decoders active at the same time.
 */
const AutoPlayVideo = ({ src, poster, className = '' }: AutoPlayVideoProps) => {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;

    const loadAndPlay = () => {
      if (video.dataset.loadedSrc !== src) {
        video.src = src;
        video.dataset.loadedSrc = src;
        video.load();
      }

      video.play().catch(() => {});
    };

    if (!('IntersectionObserver' in window)) {
      loadAndPlay();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadAndPlay();
        } else {
          video.pause();
        }
      },
      { rootMargin: '250px 0px' },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [src]);

  return (
    <video
      ref={ref}
      poster={poster}
      className={className}
      muted
      loop
      playsInline
      preload='none'
      aria-hidden='true'
    />
  );
};

export default AutoPlayVideo;
